import produce from 'immer';
import IntervalManager from '@utils/intervalManager';
import {
  DEFAULT_FILE_SORTING_KEY,
  DOCUMENT_PREVIEW_ERROR_FILE_CONTENT_MESSAGE_404,
} from '@constants/data-page';
import { HTTPStatusCode, MessageCodes, StatusCodes } from '@src/constants/enum/common';
import {
  DELETE_ALL_FILES,
  DELETE_FILE,
  DELETE_MULTIPLE_FILES,
  DOWNLOAD_FILE,
  FULFILLED,
  GET_FILE_CONTENT,
  GET_INDEX_DOCUMENTS_PER_FILE,
  GET_INDEX_FILES,
  GET_SAMPLE_DATASET_LIST,
  GET_WORKSPACE_FILES,
  IAPIPaginationData,
  IDocumentsPerFile,
  IFile,
  IMessage,
  ISelectedFileDocument,
  PENDING,
  POLLING_INDEX_FILES,
  REJECTED,
  RESET_FILE_CONTENT,
  RESET_FILES_MESSAGE,
  RETRY_INDEX_BY_STATUS,
  RETRY_INDEX_FILE,
  SELECT_FILE_DOCUMENT,
  SELECT_FILE_SORT_VALUE,
  SET_POLLING_INTERVAL,
  STOP_POLLING,
  UPDATE_FILE_META,
} from '../types/types';

export type PollingKey = string;

export const POLLING_KEYS = {
  INDEX_FILES: POLLING_INDEX_FILES,
} as const;

interface IInitialStateProps {
  files: IAPIPaginationData<IFile[]>;
  documentsPerFile: IDocumentsPerFile[];
  message: IMessage;
  actionStatus: StatusCodes;
  selectedFileDocument: ISelectedFileDocument | null;
  fileContent: string | null;
  getFileContentStatus: StatusCodes;
  getFileContentError: string | null;
  fetchStatus: StatusCodes;
  updateFileMetaStatus: StatusCodes;
  sortValue: string;
  sampleData: any;
  pollingIntervals: Record<PollingKey, number>;
  retryIndexFileStatus: StatusCodes;
}

export const initialState: IInitialStateProps = {
  files: {
    data: [],
    has_more: false,
    total: 0,
  },
  documentsPerFile: [],
  message: {
    type: MessageCodes.INFO,
    content: '',
  },
  actionStatus: StatusCodes.IDLE,
  selectedFileDocument: null,
  fileContent: null,
  getFileContentError: null,
  getFileContentStatus: StatusCodes.IDLE,
  fetchStatus: StatusCodes.IDLE,
  updateFileMetaStatus: StatusCodes.IDLE,
  sortValue: DEFAULT_FILE_SORTING_KEY,
  sampleData: null,
  pollingIntervals: {
    [POLLING_KEYS.INDEX_FILES]: 10_000,
  },
  retryIndexFileStatus: StatusCodes.IDLE,
};

function layoutReducer(state = initialState, action: any) {
  return produce(state, (draft) => {
    const localDraft = draft;
    switch (action.type) {
      case SET_POLLING_INTERVAL:
        localDraft.pollingIntervals[action.payload.key] = action.payload.interval;
        break;
      case STOP_POLLING:
        const intervalManager = IntervalManager.getInstance();
        intervalManager.clear(action.payload);
        break;
      case RESET_FILES_MESSAGE:
        localDraft.message = initialState.message;
        localDraft.actionStatus = StatusCodes.IDLE;
        localDraft.fetchStatus = StatusCodes.IDLE;
        break;
      case RESET_FILE_CONTENT:
        localDraft.fileContent = initialState.fileContent;
        localDraft.getFileContentStatus = StatusCodes.IDLE;
        break;
      case SELECT_FILE_SORT_VALUE:
        localDraft.sortValue = action.payload;
        break;
      case SELECT_FILE_DOCUMENT:
        localDraft.selectedFileDocument = action.payload;
        break;
      case `${DELETE_FILE}/${PENDING}`:
      case `${DELETE_MULTIPLE_FILES}/${PENDING}`:
      case `${DELETE_ALL_FILES}/${PENDING}`:
        localDraft.actionStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${GET_FILE_CONTENT}/${PENDING}`:
        localDraft.getFileContentStatus = StatusCodes.IN_PROGRESS;
        localDraft.getFileContentError = null;
        break;
      case `${DOWNLOAD_FILE}/${PENDING}`:
        localDraft.message = initialState.message;
        localDraft.actionStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${GET_FILE_CONTENT}/${FULFILLED}`:
        localDraft.getFileContentStatus = StatusCodes.SUCCESS;
        localDraft.fileContent = action.payload;
        localDraft.getFileContentError = null;
        break;
      case `${GET_FILE_CONTENT}/${REJECTED}`:
        localDraft.getFileContentStatus = StatusCodes.ERROR;
        if (action.payload?.status === HTTPStatusCode.NOT_FOUND) {
          localDraft.getFileContentError = DOCUMENT_PREVIEW_ERROR_FILE_CONTENT_MESSAGE_404;
        } else {
          localDraft.getFileContentError =
            action.payload.response.data.errors[0] || action.payload?.message || '';
        }
        break;
      case `${DOWNLOAD_FILE}/${FULFILLED}`:
        localDraft.actionStatus = StatusCodes.SUCCESS;
        break;
      case `${GET_INDEX_FILES}/${PENDING}`:
      case `${GET_WORKSPACE_FILES}/${PENDING}`:
        localDraft.fetchStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${GET_INDEX_FILES}/${FULFILLED}`:
      case `${GET_WORKSPACE_FILES}/${FULFILLED}`:
        localDraft.fetchStatus = StatusCodes.SUCCESS;
        localDraft.files = action.payload;
        break;
      case `${GET_INDEX_FILES}/${REJECTED}`:
      case `${GET_WORKSPACE_FILES}/${REJECTED}`:
        localDraft.fetchStatus = StatusCodes.ERROR;
        break;
      case `${GET_INDEX_DOCUMENTS_PER_FILE}/${PENDING}`:
        localDraft.fetchStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${GET_INDEX_DOCUMENTS_PER_FILE}/${FULFILLED}`:
        localDraft.fetchStatus = StatusCodes.SUCCESS;
        localDraft.documentsPerFile = action.payload;
        break;
      case `${GET_INDEX_DOCUMENTS_PER_FILE}/${REJECTED}`:
        localDraft.fetchStatus = StatusCodes.ERROR;
        break;
      case `${DELETE_FILE}/${FULFILLED}`:
      case `${DELETE_ALL_FILES}/${FULFILLED}`:
      case `${DELETE_MULTIPLE_FILES}/${FULFILLED}`:
        localDraft.actionStatus = StatusCodes.SUCCESS;
        break;
      case `${GET_SAMPLE_DATASET_LIST}/${FULFILLED}`:
        localDraft.sampleData = action.payload;
        break;
      case `${DELETE_FILE}/${REJECTED}`:
      case `${DELETE_MULTIPLE_FILES}/${REJECTED}`:
      case `${DELETE_ALL_FILES}/${REJECTED}`:
      case `${DOWNLOAD_FILE}/${REJECTED}`:
        localDraft.actionStatus = StatusCodes.ERROR;
        break;

      case `${UPDATE_FILE_META}/${PENDING}`:
        localDraft.updateFileMetaStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${UPDATE_FILE_META}/${FULFILLED}`:
        localDraft.updateFileMetaStatus = StatusCodes.SUCCESS;
        break;
      case `${UPDATE_FILE_META}/${REJECTED}`:
        localDraft.updateFileMetaStatus = StatusCodes.ERROR;
        break;
      case `${RETRY_INDEX_BY_STATUS}/${PENDING}`:
      case `${RETRY_INDEX_FILE}/${PENDING}`:
        localDraft.retryIndexFileStatus = StatusCodes.IN_PROGRESS;
        break;
      case `${RETRY_INDEX_BY_STATUS}/${FULFILLED}`:
      case `${RETRY_INDEX_FILE}/${FULFILLED}`:
        localDraft.retryIndexFileStatus = StatusCodes.SUCCESS;
        break;
      case `${RETRY_INDEX_BY_STATUS}/${REJECTED}`:
      case `${RETRY_INDEX_FILE}/${REJECTED}`:
        localDraft.retryIndexFileStatus = StatusCodes.ERROR;
        break;

      default:
        break;
    }

    return localDraft;
  });
}

export default layoutReducer;
